import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Badge, Banner, Button, Card, EmptyState, Mono, PageBody, PageHeader, StackedList } from "../ui/Field";

type CollectionDef = {
  id: string;
  key: string;
  name: string;
  name_plural: string;
  schema_json: Array<{ name: string; label: string; kind: string; required?: boolean }>;
  is_taxonomy: number;
  listing_path: string | null;
  slug_field: string | null;
};
type Entry = { id: string; slug: string; status: string; draft_revision: number; live_revision: number | null; updated_at: number };

export default function CollectionDetail() {
  const { slug, col } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState<CollectionDef | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!slug || !col) return;
    Promise.all([
      api.get<CollectionDef>(`/api/sites/${slug}/collections/${col}`),
      api.get<{ items: Entry[] }>(`/api/sites/${slug}/collections/${col}/entries`),
    ]).then(([def, ents]) => { setC(def); setEntries(ents.items); }).catch((e) => setErr((e as Error).message));
  }, [slug, col]);

  async function createEntry() {
    const s = prompt("Slug for new entry:");
    if (!s) return;
    try {
      const r = await api.post<{ id: string }>(`/api/sites/${slug}/collections/${col}/entries`, { slug: s });
      nav(`/sites/${slug}/collections/${col}/entries/${r.id}`);
    } catch (e) { setErr((e as Error).message); }
  }
  async function remove() {
    if (!c) return;
    if (!confirm(`Delete collection "${c.name_plural}" and all its entries?`)) return;
    try { await api.delete(`/api/sites/${slug}/collections/${col}`); nav(`/sites/${slug}/collections`); }
    catch (e) { setErr((e as Error).message); }
  }

  if (err && !c) return <PageBody><Banner kind="error">{err}</Banner></PageBody>;
  if (!c) return <PageBody><div className="h-32 bg-[var(--color-paper-2)] animate-pulse rounded" /></PageBody>;

  return (
    <PageBody>
      <Link to={`/sites/${slug}/collections`} className="text-[var(--color-muted)] hover:text-[var(--color-ink)] text-[12px] no-underline">← Collections</Link>
      <PageHeader
        eyebrow={`№ Collection · ${c.key}`}
        title={c.name_plural}
        description={c.is_taxonomy ? "A taxonomy. Used as tags or categories on other collections." : "A content type. Entries can be referenced from pages and other entries."}
        actions={
          <>
            <Button onClick={createEntry}>+ Entry</Button>
            <Button variant="danger" onClick={remove}>Delete</Button>
          </>
        }
      />
      {err && <Banner kind="error">{err}</Banner>}

      <Card number="S1" title="Schema">
        <table className="w-full text-[13.5px] border-collapse">
          <thead>
            <tr className="text-left label-tracked border-b border-[var(--color-rule)]">
              <th className="pb-2.5">Field</th>
              <th className="pb-2.5">Label</th>
              <th className="pb-2.5">Kind</th>
              <th className="pb-2.5">Required</th>
            </tr>
          </thead>
          <tbody>
            {c.schema_json.map((f) => (
              <tr key={f.name} className="border-b border-[var(--color-rule)] last:border-0">
                <td className="py-2.5"><Mono>{f.name}</Mono></td>
                <td className="py-2.5">{f.label}</td>
                <td className="py-2.5"><Badge>{f.kind}</Badge></td>
                <td className="py-2.5 text-[12px] text-[var(--color-muted)]">{f.required ? "Yes" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card number="E1" title={`Entries (${entries.length})`} className="mt-6" bodyClassName="p-0">
        {entries.length === 0 ? (
          <div className="p-6"><EmptyState title="No entries yet." action={<Button onClick={createEntry}>+ Entry</Button>} /></div>
        ) : (
          <StackedList className="px-5">
            {entries.map((e) => (
              <li key={e.id} className="flex items-center justify-between gap-4 py-3.5">
                <Link to={`/sites/${slug}/collections/${col}/entries/${e.id}`} className="flex-1 no-underline group">
                  <span className="block text-[14px] text-[var(--color-ink)] group-hover:text-[var(--color-accent)] transition-colors">/{e.slug}</span>
                  <span className="block text-[11px] text-[var(--color-muted)] font-mono mt-0.5">draft {e.draft_revision}{e.live_revision ? ` · live ${e.live_revision}` : " · not published"}</span>
                </Link>
                <Badge tone={e.status === "published" ? "success" : e.status === "review" ? "warning" : "neutral"}>{e.status}</Badge>
              </li>
            ))}
          </StackedList>
        )}
      </Card>
    </PageBody>
  );
}
